import { useState } from 'react';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-14 w-14',
  xl: 'h-20 w-20',
};

/** Pixel size per AvatarSize, given to the <img> so the layout doesn't shift while it loads. */
const pixels: Record<AvatarSize, number> = { xs: 24, sm: 32, md: 40, lg: 56, xl: 80 };

const initialsText: Record<AvatarSize, string> = {
  xs: 'text-[9px]',
  sm: 'text-[11px]',
  md: 'text-xs',
  lg: 'text-base',
  xl: 'text-xl',
};

interface AvatarProps {
  /** Without an image, the person's initials (from `alt`) are shown instead. */
  src?: string | null;
  alt: string;
  size?: AvatarSize;
  className?: string;
}

/**
 * Laravel stores only the file name in `users.profile_image`; its User model adds the
 * public storage host and `images/products/` (getProfileImageAttribute). The agency API
 * reads the table directly and sends the bare name, so it is completed here when the
 * host is configured (VITE_PROFILE_IMAGE_BASE_URL = production's AWS_URL).
 */
const IMAGE_BASE = import.meta.env.VITE_PROFILE_IMAGE_BASE_URL?.replace(/\/$/, '');

// Open the connection to the image host once, before the first avatar asks for it.
if (IMAGE_BASE && typeof document !== 'undefined') {
  try {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = new URL(IMAGE_BASE).origin;
    document.head.appendChild(link);
  } catch {
    // A malformed setting only costs the head start, never the page.
  }
}

function imageUrl(src: string | null | undefined): string | null {
  if (!src) return null;
  if (/^(https?:|data:|blob:|\/)/.test(src)) return src;
  return IMAGE_BASE ? `${IMAGE_BASE}/images/products/${src}` : null;
}

export default function Avatar({ src, alt, size = 'md', className = '' }: AvatarProps) {
  const url = imageUrl(src);
  // An image that fails to load falls back to the initials instead of a broken icon.
  const [failed, setFailed] = useState<string | null>(null);
  if (!url || failed === url) {
    const initials = alt
      .split(/\s+/)
      .map((word) => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
    return (
      <span
        title={alt}
        aria-label={alt}
        className={`inline-flex shrink-0 items-center justify-center rounded-full bg-primary-100 font-semibold text-primary-800 ${sizeClasses[size]} ${initialsText[size]} ${className}`}
      >
        {initials}
      </span>
    );
  }
  return (
    <img
      src={url}
      onError={() => setFailed(url)}
      // Lists can hold hundreds of avatars: fetch each as it scrolls into view.
      loading="lazy"
      decoding="async"
      width={pixels[size]}
      height={pixels[size]}
      alt={alt}
      title={alt}
      className={`shrink-0 rounded-full object-cover bg-background-200 ${sizeClasses[size]} ${className}`}
    />
  );
}